import { useState, useEffect, useCallback, type ReactNode } from 'react';
import { formatDate } from '../lib/dates';
import { ApiError, asList } from '../lib/api';
import {
  portalApi, loadRazorpay, rupees,
  type PortalStudent, type AttendanceSummary, type PortalInvoice, type PortalResults, type StartPay,
  type PortalTimetable, type PortalProfile, type PortalHoliday,
  type PortalDocument, type PortalRemark,
  type PortalReportCard, type PortalAllocation,
  type PortalStorefrontItem, type PortalOrder,
} from '../lib/portalApi';
import { MyHouseCard } from '../components/MyHouseCard';
import { portalTripsApi, type TripConsent } from '../lib/tripsApi';
import { shipmentsApi, type Shipment } from '../lib/storeApi';
import { requestsApi, type StudentRequest } from '../lib/requestsApi';
import { downloadFile } from '../lib/downloadsApi';
import { InstallBanner } from '../components/InstallPrompt';
import { useT } from '../context/I18nContext';

type Tab = 'attendance' | 'fees' | 'results' | 'timetable' | 'profile' | 'holidays' | 'remarks' | 'reportcards' | 'store' | 'trips' | 'downloads' | 'requests';

/// Family (Parent/Student) portal. A parent picks a child; a student sees themselves. All data is
/// scoped server-side to the caller's linked students — this UI only ever asks for a studentId it
/// got from /api/portal/children, and the backend re-checks every read.
export function FamilyPortal() {
  const t = useT();
  const [children, setChildren] = useState<PortalStudent[]>([]);
  const [active, setActive] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('attendance');
  const [loading, setLoading] = useState(true);
  const [linking, setLinking] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const kids = await portalApi.myChildren();
      setChildren(kids);
      setActive((cur) => cur ?? (kids[0]?.id ?? null));
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;

  return (
    <div>
      <InstallBanner />

      <div style={{ display: 'flex', alignItems: 'flex-start', marginBottom: 20 }}>
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>{t('Family')}</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>{t('My family')}</h1>
        </div>
        <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setLinking(true)}>+ Link a child</button>
      </div>

      {children.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: 'center', color: 'var(--ink-2)' }}>
          No children linked yet. Ask your school for a link code, then tap <strong>{t('Link a child')}</strong>.
        </div>
      ) : (
        <>
          {children.length > 1 && (
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {children.map((c) => (
                <button key={c.id} className={`btn ${active === c.id ? 'btn-accent' : ''}`}
                  style={{ fontSize: 13 }} onClick={() => setActive(c.id)}>{c.fullName}</button>
              ))}
            </div>
          )}

          {active && (
            <>
              <div className="tab-strip" style={{ gap: 4, marginBottom: 16 }}>
                <TabBtn active={tab === 'attendance'} onClick={() => setTab('attendance')}>{t('Attendance')}</TabBtn>
                <TabBtn active={tab === 'fees'} onClick={() => setTab('fees')}>{t('Fees')}</TabBtn>
                <TabBtn active={tab === 'results'} onClick={() => setTab('results')}>{t('Results')}</TabBtn>
                <TabBtn active={tab === 'timetable'} onClick={() => setTab('timetable')}>{t('Timetable')}</TabBtn>
                <TabBtn active={tab === 'profile'} onClick={() => setTab('profile')}>{t('Profile')}</TabBtn>
                <TabBtn active={tab === 'holidays'} onClick={() => setTab('holidays')}>{t('Holidays')}</TabBtn>
                <TabBtn active={tab === 'remarks'} onClick={() => setTab('remarks')}>{t('Remarks')}</TabBtn>
                <TabBtn active={tab === 'reportcards'} onClick={() => setTab('reportcards')}>{t('Report cards')}</TabBtn>
                <TabBtn active={tab === 'store'} onClick={() => setTab('store')}>{t('Store')}</TabBtn>
                <TabBtn active={tab === 'trips'} onClick={() => setTab('trips')}>{t('Trips')}</TabBtn>
                <TabBtn active={tab === 'downloads'} onClick={() => setTab('downloads')}>{t('Downloads')}</TabBtn>
                <TabBtn active={tab === 'requests'} onClick={() => setTab('requests')}>{t('Requests')}</TabBtn>
              </div>
              {/* ⚠ ON THE LANDING TAB, ABOVE EVERYTHING. A child's house is not a section somebody
                  goes looking for — it is what they want to see when they open the app. It renders
                  nothing at all in a school that does not run houses. */}
              {tab === 'attendance' && <MyHouseCard studentId={active} />}
              {tab === 'attendance' && <AttendanceTab studentId={active} />}
              {tab === 'fees' && <FeesTab studentId={active} />}
              {tab === 'results' && <ResultsTab studentId={active} />}
              {tab === 'timetable' && <TimetableTab studentId={active} />}
              {tab === 'profile' && <ProfileTab studentId={active} />}
              {tab === 'holidays' && <HolidaysTab studentId={active} />}
              {tab === 'remarks' && <RemarksTab studentId={active} />}
              {tab === 'reportcards' && <ReportCardsTab studentId={active} />}
              {tab === 'store' && <StoreTab studentId={active} />}
              {tab === 'trips' && <TripsTab studentId={active} />}
              {tab === 'downloads' && <DownloadsTab />}
              {tab === 'requests' && <RequestsTab studentId={active} />}
            </>
          )}
        </>
      )}

      {linking && <LinkDialog onClose={() => setLinking(false)} onDone={() => { setLinking(false); load(); }} />}
    </div>
  );
}

function AttendanceTab({ studentId }: { studentId: string }) {
  const t = useT();
  const [data, setData] = useState<AttendanceSummary | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setData(null); setErr(null);
    portalApi.attendance(studentId).then(setData)
      .catch((e) => setErr(e instanceof ApiError && e.status === 403 ? 'You don’t have access to this student.' : 'Could not load attendance.'));
  }, [studentId]);
  if (err) return <Card muted>{err}</Card>;
  if (!data) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <Stat label="Attendance" value={`${data.percentage}%`} />
        <Stat label="Present" value={String(data.presentDays)} />
        <Stat label="Absent" value={String(data.absentDays)} />
        <Stat label="Days recorded" value={String(data.totalDays)} />
      </div>
      <Card>
        <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 8, fontWeight: 600 }}>{t('Recent days')}</div>
        {data.recentDays.length === 0 ? <span style={{ color: 'var(--ink-3)' }}>{t('No records yet.')}</span> : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {data.recentDays.map((d) => (
              <span key={d.date} className="pill" style={{ fontSize: 11,
                background: d.status === 'Absent' ? 'rgba(193,39,45,.1)' : 'rgba(45,140,80,.1)',
                color: d.status === 'Absent' ? 'var(--danger,#C1272D)' : 'var(--ledger-green,#2D8C50)' }}>
                {d.date.slice(5)} · {d.status}
              </span>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function FeesTab({ studentId }: { studentId: string }) {
  const t = useT();
  const [rows, setRows] = useState<PortalInvoice[] | null>(null);
  const [payEnabled, setPayEnabled] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [paying, setPaying] = useState<string | null>(null);

  const load = useCallback(() => {
    setRows(null); setErr(null);
    portalApi.fees(studentId).then((r) => setRows(asList(r)))
      .catch((e) => setErr(e instanceof ApiError && e.status === 403 ? 'You don’t have access to this student.' : 'Could not load fees.'));
    portalApi.payEnabled().then((r) => setPayEnabled(r.enabled)).catch(() => setPayEnabled(false));
  }, [studentId]);
  useEffect(load, [load]);

  const pay = async (inv: PortalInvoice) => {
    setPaying(inv.id);
    try {
      const ok = await loadRazorpay();
      if (!ok) { alert('Could not load the payment window. Check your connection.'); return; }
      const order: StartPay = await portalApi.startPay(inv.id);
      const rzp = new (window as unknown as { Razorpay: new (o: unknown) => { open: () => void } }).Razorpay({
        key: order.keyId,
        amount: order.amountPaise,
        currency: order.currency,
        name: 'School fees',
        description: `Invoice ${order.invoiceNumber}`,
        order_id: order.orderId,
        handler: async (resp: { razorpay_order_id: string; razorpay_payment_id: string; razorpay_signature: string }) => {
          try {
            await portalApi.verifyPay({
              invoiceId: inv.id, amountPaise: order.amountPaise, orderId: resp.razorpay_order_id,
              paymentId: resp.razorpay_payment_id, signature: resp.razorpay_signature,
            });
            load(); // refresh balances after a verified payment
          } catch { alert('Payment could not be verified. If money was deducted, it will be reversed.'); }
        },
        theme: { color: '#C1272D' },
      });
      rzp.open();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : 'Could not start the payment.');
    } finally { setPaying(null); }
  };

  if (err) return <Card muted>{err}</Card>;
  if (!rows) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  if (rows.length === 0) return <Card muted>{t('No invoices yet.')}</Card>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {rows.map((inv) => (
        <Card key={inv.id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div>
              <div style={{ fontWeight: 600 }}>{inv.number} <span style={{ color: 'var(--ink-3)', fontWeight: 400, fontSize: 12 }}>· {inv.academicYear}</span></div>
              <div style={{ fontSize: 12, color: 'var(--ink-2)', marginTop: 2 }}>
                Total {rupees(inv.totalPaise)} · Paid {rupees(inv.paidPaise)}
                {inv.dueDate && <> · Due {inv.dueDate}</>}
              </div>
            </div>
            <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
              <div style={{ fontWeight: 700, color: inv.balancePaise > 0 ? 'var(--danger,#C1272D)' : 'var(--ledger-green,#2D8C50)' }}>
                {inv.balancePaise > 0 ? `${rupees(inv.balancePaise)} due` : 'Paid'}
              </div>
              {inv.balancePaise > 0 && payEnabled && (
                <button className="btn btn-accent" style={{ marginTop: 6, fontSize: 13 }} disabled={paying === inv.id} onClick={() => pay(inv)}>
                  {paying === inv.id ? 'Opening…' : 'Pay online'}
                </button>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function ResultsTab({ studentId }: { studentId: string }) {
  const t = useT();
  const [data, setData] = useState<PortalResults | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    setData(null); setErr(null);
    portalApi.results(studentId).then(setData)
      .catch((e) => setErr(e instanceof ApiError && e.status === 403 ? 'You don’t have access to this student.' : 'Could not load results.'));
  }, [studentId]);
  if (err) return <Card muted>{err}</Card>;
  if (!data) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  if (data.results.length === 0) return <Card muted>{t('No published results yet.')}</Card>;
  return (
    <div>
      {data.provisional && (
        <div className="pill" style={{ background: 'rgba(200,150,0,.12)', color: 'var(--amber,#B8860B)', padding: '8px 12px', borderRadius: 8, marginBottom: 12, fontSize: 12, justifyContent: 'flex-start' }}>
          Results are provisional until the school confirms its grading. Not an official transcript.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {data.results.map((r, i) => (
          <Card key={i}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <div style={{ fontWeight: 600 }}>{r.semester}</div>
              <div style={{ marginLeft: 'auto', color: 'var(--ink-2)', fontSize: 13 }}>{r.rawMarks}</div>
              <span className="pill pill-muted" style={{ fontSize: 11, marginLeft: 10 }}>{r.label}</span>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

function LinkDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const t = useT();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    if (!code.trim()) return;
    setBusy(true); setError(null);
    try { await portalApi.redeemCode(code.trim()); onDone(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not link with that code.'); }
    finally { setBusy(false); }
  };
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50 }} onClick={onClose}>
      <div className="card" style={{ padding: 24, width: 380, maxWidth: '90vw' }} onClick={(e) => e.stopPropagation()}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 8 }}>{t('Link a child')}</h2>
        <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 16 }}>{t('Enter the code your school gave you.')}</p>
        {error && <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{error}</div>}
        <input className="input mono" autoFocus value={code} onChange={(e) => setCode(e.target.value.toUpperCase())}
          placeholder="ABCD2345" style={{ marginBottom: 20, letterSpacing: '.1em' }}
          onKeyDown={(e) => e.key === 'Enter' && code.trim() && submit()} />
        <div style={{ display: 'flex', gap: 10 }}>
          <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onClose}>{t('Cancel')}</button>
          <button className="btn btn-accent" style={{ flex: 1, justifyContent: 'center' }} disabled={!code.trim() || busy} onClick={submit}>
            {busy ? 'Linking…' : 'Link'}
          </button>
        </div>
      </div>
    </div>
  );
}

function RequestsTab({ studentId }: { studentId: string }) {
  const t = useT();
  const [rows, setRows] = useState<StudentRequest[]>([]);
  const [kind, setKind] = useState<'Leave' | 'DressCode' | 'DropChange' | 'Other'>('Leave');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  // shared fields
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [forDate, setForDate] = useState('');
  const [location, setLocation] = useState('');
  const [title, setTitle] = useState('');
  const [detail, setDetail] = useState('');

  const load = useCallback(() => {
    requestsApi.mine().then((all) => setRows(all.filter((r) => r.studentId === studentId))).catch(() => setRows([]));
  }, [studentId]);
  useEffect(load, [load]);

  const reset = () => { setFromDate(''); setToDate(''); setForDate(''); setLocation(''); setTitle(''); setDetail(''); };

  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      if (kind === 'Leave') {
        if (!fromDate || !toDate) { setBusy(false); return; }
        await requestsApi.leave({ studentId, fromDate, toDate, reason: detail.trim() || null });
      } else if (kind === 'DressCode') {
        if (!detail.trim()) { setBusy(false); return; }
        await requestsApi.dressCode({ studentId, detail: detail.trim() });
      } else if (kind === 'DropChange') {
        if (!forDate || !location.trim()) { setBusy(false); return; }
        await requestsApi.dropChange({ studentId, forDate, location: location.trim(), reason: detail.trim() || null });
      } else {
        if (!title.trim()) { setBusy(false); return; }
        await requestsApi.other({ studentId, title: title.trim(), detail: detail.trim() || null });
      }
      reset(); setMsg('Request sent to the school.'); load();
    } catch (e) { setMsg(e instanceof ApiError ? e.message : 'Could not submit the request.'); }
    finally { setBusy(false); }
  };

  const badge = (s: string) => s === 'Approved' ? 'pill-ok' : s === 'Declined' ? 'pill-danger' : 'pill-muted';
  const KINDS: { k: typeof kind; label: string }[] = [
    { k: 'Leave', label: 'Leave' }, { k: 'DressCode', label: 'Dress-code' },
    { k: 'DropChange', label: 'Drop-change' }, { k: 'Other', label: 'Other' },
  ];

  return (
    <div>
      <div className="card" style={{ padding: 18, marginBottom: 16, maxWidth: 540 }}>
        <h3 style={{ fontSize: 15, marginBottom: 10 }}>{t('Raise a request')}</h3>
        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {KINDS.map(({ k, label }) => (
            <button key={k} onClick={() => setKind(k)} className="btn" style={{ padding: '4px 12px', fontSize: 13, background: kind === k ? 'var(--accent,#C1272D)' : undefined, color: kind === k ? '#fff' : undefined }}>{label}</button>
          ))}
        </div>
        {msg && <div className="pill pill-muted" style={{ padding: '8px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>{msg}</div>}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {kind === 'Leave' && (
            <div style={{ display: 'flex', gap: 8 }}>
              <div style={{ flex: 1 }}><label className="label">{t('From')}</label><input className="input" type="date" value={fromDate} onChange={(e) => setFromDate(e.target.value)} /></div>
              <div style={{ flex: 1 }}><label className="label">To</label><input className="input" type="date" value={toDate} onChange={(e) => setToDate(e.target.value)} /></div>
            </div>
          )}
          {kind === 'DropChange' && (
            <>
              <div><label className="label">{t('Date')}</label><input className="input" type="date" value={forDate} onChange={(e) => setForDate(e.target.value)} /></div>
              <div><label className="label">{t('Drop location')}</label><input className="input" placeholder="e.g. Grandparents' house, 4th Cross Jayanagar" value={location} onChange={(e) => setLocation(e.target.value)} /></div>
              <p style={{ fontSize: 11, color: 'var(--ink-3)' }}>For your child's safety, the school must approve any change to the usual drop before the driver acts on it.</p>
            </>
          )}
          {kind === 'Other' && (
            <div><label className="label">{t('Title')}</label><input className="input" placeholder="Short summary" value={title} onChange={(e) => setTitle(e.target.value)} /></div>
          )}
          <div>
            <label className="label">{kind === 'DressCode' ? 'What & why' : 'Reason / details (optional)'}</label>
            <input className="input" value={detail} onChange={(e) => setDetail(e.target.value)} placeholder={kind === 'DressCode' ? 'e.g. sports shoes for PT on Fridays (foot injury)' : ''} />
          </div>
          <button className="btn btn-accent" disabled={busy} onClick={submit} style={{ alignSelf: 'flex-start' }}>{busy ? 'Sending…' : 'Send request'}</button>
        </div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>{t('Your requests')}</div>
      {rows.length === 0 ? <p style={{ fontSize: 13, color: 'var(--ink-3)' }}>{t('No requests yet.')}</p> : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 540 }}>
          {rows.map((r) => (
            <div key={r.id} className="card" style={{ padding: 14, display: 'flex', alignItems: 'center', gap: 10 }}>
              <div>
                <strong style={{ fontSize: 14 }}>{r.title}</strong>
                <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                  {r.type === 'Leave' && r.fromDate ? `${r.fromDate} → ${r.toDate}` : ''}
                  {r.type === 'DropChange' && r.forDate ? `${r.forDate} · ${r.location ?? ''}` : ''}
                  {r.detail ? ` · ${r.detail}` : ''}
                  {r.reviewNote ? ` · ${r.reviewNote}` : ''}
                </div>
              </div>
              <span className={`pill ${badge(r.status)}`} style={{ marginLeft: 'auto', fontSize: 11 }}>{r.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function TimetableTab({ studentId }: { studentId: string }) {
  const t = useT();
  const [tt, setTt] = useState<PortalTimetable | null>(null);
  const [none, setNone] = useState(false);
  useEffect(() => {
    setTt(null); setNone(false);
    portalApi.timetable(studentId).then((r) => {
      if ('available' in r && r.available === false) setNone(true);
      else setTt(r as PortalTimetable);
    }).catch(() => setNone(true));
  }, [studentId]);

  if (none) return <Card muted>{t('No timetable published for this class yet.')}</Card>;
  if (!tt) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {tt.days.map((d) => (
        <Card key={d.day}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>{d.day}</div>
          <div style={{ display: 'grid', gap: 6 }}>
            {d.slots.map((s, i) => (
              <div key={i} style={{ display: 'flex', gap: 12, alignItems: 'center', fontSize: 14 }}>
                <span style={{ minWidth: 90, color: 'var(--ink-3)', fontSize: 12 }}>{s.time ?? `Period ${s.periodNumber}`}</span>
                <span style={{ fontWeight: 600, flex: 1 }}>{s.subject}</span>
                {s.teacher && <span style={{ color: 'var(--ink-2)', fontSize: 13 }}>{s.teacher}</span>}
                {s.room && <span style={{ color: 'var(--ink-3)', fontSize: 12 }}>{s.room}</span>}
              </div>
            ))}
          </div>
        </Card>
      ))}
    </div>
  );
}

function ProfileTab({ studentId }: { studentId: string }) {
  const [p, setP] = useState<PortalProfile | null>(null);
  const [posts, setPosts] = useState<Array<{ id: string; postTitle: string; academicYear: string; viaElection: boolean }>>([]);
  useEffect(() => {
    setP(null); portalApi.profile(studentId).then(setP).catch(() => setP(null));
    import('../lib/leadershipApi').then((m) => m.leadershipApi.forStudent(studentId).then((r) => setPosts(asList(r))).catch(() => setPosts([])));
  }, [studentId]);
  if (!p) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  const rows: Array<[string, string | null]> = [
    ['Name', p.fullName], ['Admission no.', p.admissionNumber], ['Class', p.classLabel],
    ['Status', p.status], ['Guardian', p.guardianName], ['Guardian mobile', p.guardianMobile], ['Email', p.email],
  ];
  return (
    <Card>
      {posts.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
          {posts.map((a) => (
            <span key={a.id} className="pill" style={{ fontSize: 12, background: '#fff4e5', color: '#b26a00' }}>
              {a.postTitle}{a.viaElection ? ' · Elected' : ''}
            </span>
          ))}
        </div>
      )}
      <div style={{ display: 'grid', gap: 10 }}>
        {rows.filter(([, v]) => v).map(([k, v]) => (
          <div key={k} style={{ display: 'flex', gap: 12 }}>
            <span style={{ minWidth: 130, color: 'var(--ink-3)', fontSize: 13 }}>{k}</span>
            <span style={{ fontWeight: 600 }}>{v}</span>
          </div>
        ))}
      </div>
    </Card>
  );
}

function HolidaysTab({ studentId }: { studentId: string }) {
  const t = useT();
  const [rows, setRows] = useState<PortalHoliday[] | null>(null);
  useEffect(() => { setRows(null); portalApi.holidays(studentId).then((r) => setRows(asList(r))).catch(() => setRows([])); }, [studentId]);
  if (!rows) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  if (rows.length === 0) return <Card muted>{t('No upcoming holidays listed.')}</Card>;
  const fmt = (d: string) => new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((h, i) => (
        <Card key={i}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{h.name}</div>
              {h.note && <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{h.note}</div>}
            </div>
            <div style={{ fontSize: 13, color: 'var(--ink-2)', textAlign: 'right' }}>
              {fmt(h.date)}{h.endDate && h.endDate !== h.date ? ` – ${fmt(h.endDate)}` : ''}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function TripsTab({ studentId }: { studentId: string }) {
  const t = useT();
  const [rows, setRows] = useState<TripConsent[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const load = useCallback(() => {
    portalTripsApi.forStudent(studentId).then((r) => setRows(asList(r))).catch(() => setRows([]));
  }, [studentId]);
  useEffect(load, [load]);

  const respond = async (c: TripConsent, yes: boolean) => {
    setBusy(c.id); setMsg(null);
    try {
      let phone: string | null = null, med: string | null = null;
      if (yes) {
        phone = window.prompt('Emergency contact number for the day (the teacher will carry this):') || null;
        med = window.prompt('Any medical note the staff should know? (allergies, medication — leave blank if none)') || null;
      }
      await portalTripsApi.respond(c.tripId, {
        studentId, consent: yes, emergencyPhone: phone, medicalNote: med, note: null,
      });
      load();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : 'Could not save your reply.');
    } finally { setBusy(null); }
  };

  const withdraw = async (c: TripConsent) => {
    if (!window.confirm('Withdraw permission for this trip?')) return;
    setBusy(c.id); setMsg(null);
    try { await portalTripsApi.withdraw(c.tripId, studentId, null); load(); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : 'Could not withdraw.'); }
    finally { setBusy(null); }
  };

  if (!rows) return <p style={{ color: 'var(--ink-3)' }}>{t('Loading…')}</p>;
  if (rows.length === 0) return <Card muted>{t('No trips at the moment.')}</Card>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {msg && <Card><span style={{ color: '#c1272d', fontSize: 13 }}>{msg}</span></Card>}
      {rows.map((c) => (
        <Card key={c.id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, flex: 1 }}>{c.studentName}</span>
            {c.state === 'Granted' && <span className="pill" style={{ fontSize: 11, background: '#e7f6ec', color: '#1a7f37' }}>{t('Going')}</span>}
            {c.state === 'Declined' && <span className="pill" style={{ fontSize: 11, background: '#fdecec', color: '#c1272d' }}>{t('Not going')}</span>}
            {c.state === 'Pending' && <span className="pill" style={{ fontSize: 11 }}>{t('Your reply needed')}</span>}
            {c.state === 'Withdrawn' && <span className="pill" style={{ fontSize: 11 }}>{t('Withdrawn')}</span>}
          </div>

          {c.state === 'Pending' && (
            <>
              <div style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 10 }}>
                {t('The school needs your permission before your child can go on this trip.')}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-accent" disabled={busy === c.id} onClick={() => respond(c, true)}>
                  {t('Yes, my child may go')}
                </button>
                <button className="btn" disabled={busy === c.id} onClick={() => respond(c, false)}>
                  {t('No')}
                </button>
              </div>
            </>
          )}

          {c.state === 'Granted' && (
            <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
              {c.emergencyPhone && <div>{t('Emergency contact')}: {c.emergencyPhone}</div>}
              {c.medicalNote && <div>{t('Medical note')}: {c.medicalNote}</div>}
              {c.invoiceId
                ? <div style={{ marginTop: 6, fontSize: 12 }}>{t('The trip fee is on your Payments page.')}</div>
                : <button className="btn" style={{ marginTop: 8 }} disabled={busy === c.id} onClick={() => withdraw(c)}>
                    {t('Withdraw permission')}
                  </button>}
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}


/** Where a posted parcel has got to. Only rendered when something is actually being couriered —
 *  most families collect at school and should not see an empty "deliveries" box. */
function ParcelTracker({ studentId }: { studentId: string }) {
  const t = useT();
  const [rows, setRows] = useState<Shipment[]>([]);
  useEffect(() => { shipmentsApi.forStudent(studentId).then((r) => setRows(asList(r))).catch(() => setRows([])); }, [studentId]);
  const live = rows.filter((r) => r.status !== 'Cancelled');
  if (live.length === 0) return null;

  const label = (st: string) =>
    st === 'ReadyToDispatch' ? t('Being packed')
    : st === 'Dispatched' ? t('On the way')
    : st === 'Delivered' ? t('Delivered')
    : st === 'Returned' ? t('Came back to school') : st;
  const colour = (st: string) =>
    st === 'Delivered' ? '#1a7f37' : st === 'Returned' ? '#c1272d' : st === 'Dispatched' ? '#b26a00' : 'var(--ink-3)';

  return (
    <>
      {live.map((s) => (
        <Card key={s.id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600, flex: 1 }}>{t('Your delivery')}</span>
            <span className="pill" style={{ fontSize: 11, color: colour(s.status) }}>{label(s.status)}</span>
          </div>
          <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 6 }}>
            {s.courierPartner && <div>{s.courierPartner}{s.trackingId ? ` · ${s.trackingId}` : ''}</div>}
            {s.dispatchedAt && <div>{t('Sent on')} {formatDate(s.dispatchedAt)}</div>}
            {s.deliveredAt && <div>{t('Delivered on')} {formatDate(s.deliveredAt)}{s.receivedBy ? ` · ${s.receivedBy}` : ''}</div>}
            {s.returnReason && <div style={{ color: '#c1272d' }}>{t('Please contact the school office.')}</div>}
            {s.trackingUrl && <a href={s.trackingUrl} style={{ fontSize: 12 }}>{t('Track')}</a>}
          </div>
        </Card>
      ))}
      <div style={{ height: 12 }} />
    </>
  );
}

function StoreTab({ studentId }: { studentId: string }) {
  const t = useT();
  const [rows, setRows] = useState<PortalAllocation[] | null>(null);
  const [storefront, setStorefront] = useState<PortalStorefrontItem[]>([]);
  const [orders, setOrders] = useState<PortalOrder[]>([]);
  const [h, setH] = useState(''); const [w, setW] = useState(''); const [size, setSize] = useState('');
  const [msg, setMsg] = useState<string | null>(null);
  const [ordering, setOrdering] = useState<string | null>(null);
  const year = (() => { const n = new Date(); return n.getMonth() >= 5 ? `${n.getFullYear()}-${n.getFullYear() + 1}` : `${n.getFullYear() - 1}-${n.getFullYear()}`; })();
  const load = useCallback(() => {
    portalApi.allocations(studentId).then((r) => setRows(asList(r))).catch(() => setRows([]));
    portalApi.storefront().then((r) => setStorefront(asList(r))).catch(() => setStorefront([]));
    portalApi.myOrders(studentId).then((r) => setOrders(asList(r))).catch(() => setOrders([]));
  }, [studentId]);
  useEffect(load, [load]);

  const rupees = (p: number) => '₹' + (p / 100).toLocaleString('en-IN', { minimumFractionDigits: 0 });
  const saveMeasure = async () => {
    setMsg(null);
    try { await portalApi.recordMeasurement({ studentId, academicYear: year, heightCm: Number(h), widthCm: Number(w), sizeLabel: size.trim() || null }); setMsg('Measurement saved — thank you.'); setH(''); setW(''); setSize(''); }
    catch { setMsg('Could not save.'); }
  };
  const order = async (itemId: string, mode: string) => {
    setOrdering(itemId); setMsg(null);
    try { await portalApi.placeOrder({ studentId, storeItemId: itemId, variantId: null, quantity: 1, paymentMode: mode }); setMsg(mode === 'PaidOnline' ? 'Ordered — pay from Payments.' : 'Ordered — pay at pickup.'); load(); }
    catch (e) { setMsg(e instanceof ApiError ? e.message : 'Could not order.'); } finally { setOrdering(null); }
  };

  if (!rows) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  return (
    <div>
      <ParcelTracker studentId={studentId} />
      {/* storefront: order more items */}
      {storefront.length > 0 && (
        <Card>
          <div style={{ fontWeight: 600, marginBottom: 4 }}>{t('Order from the school store')}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>In-stock items. Your child collects them in class. Pay now or at pickup.</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {storefront.map((it) => (
              <div key={it.id} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', paddingBottom: 8, borderBottom: '1px solid var(--line)' }}>
                <div style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600 }}>{it.name}</span>
                  <span style={{ marginLeft: 8, color: 'var(--ink-2)' }}>{rupees(it.pricePaise)}</span>
                  <span style={{ marginLeft: 8, fontSize: 12, color: it.stock <= 3 ? '#b26a00' : 'var(--ink-3)' }}>{it.stock} in stock</span>
                </div>
                <button className="btn" disabled={ordering === it.id} onClick={() => order(it.id, 'PayAtPickup')}>Order · pay at pickup</button>
                <button className="btn btn-accent" disabled={ordering === it.id} onClick={() => order(it.id, 'PaidOnline')}>Order · pay now</button>
              </div>
            ))}
          </div>
          {msg && <div style={{ fontSize: 13, color: 'var(--ink-2)', marginTop: 10 }}>{msg}</div>}
        </Card>
      )}

      {/* my orders */}
      {orders.length > 0 && (
        <>
          <div style={{ height: 12 }} />
          <Card>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('My orders')}</div>
            {orders.map((o) => (
              <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, padding: '4px 0' }}>
                <span style={{ flex: 1 }}>{o.itemLabel} × {o.quantity}</span>
                <span>{rupees(o.totalPaise)}</span>
                <span className="pill" style={{ fontSize: 11, background: o.status === 'HandedOver' ? '#e7f6ec' : o.status === 'Cancelled' ? '#fdecec' : 'var(--surface-2,#eee)', color: o.status === 'HandedOver' ? '#1a7f37' : o.status === 'Cancelled' ? '#c1272d' : 'var(--ink-2)' }}>
                  {o.status === 'HandedOver' ? 'Collected' : o.status}
                </span>
              </div>
            ))}
          </Card>
        </>
      )}

      <div style={{ height: 12 }} />
      <Card>
        <div style={{ fontWeight: 600, marginBottom: 8 }}>Uniform measurement ({year})</div>
        <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 10 }}>You can record your child's height and width to help the school size their uniform. The school may also measure at school.</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div><label className="label">Height (cm)</label><input className="input" type="number" value={h} onChange={(e) => setH(e.target.value)} style={{ width: 100 }} /></div>
          <div><label className="label">Width (cm)</label><input className="input" type="number" value={w} onChange={(e) => setW(e.target.value)} style={{ width: 100 }} /></div>
          <div><label className="label">Size (if known)</label><input className="input" value={size} onChange={(e) => setSize(e.target.value)} style={{ width: 90 }} /></div>
          <button className="btn btn-accent" onClick={saveMeasure}>{t('Save')}</button>
        </div>
      </Card>

      <div style={{ height: 12 }} />
      {rows.length === 0 ? <Card muted>{t('No store items allocated yet.')}</Card>
        : rows.map((a) => (
          <Card key={a.id}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <span style={{ fontWeight: 600, flex: 1 }}>School allocation · {a.academicYear}</span>
              {a.handedOver ? <span className="pill" style={{ fontSize: 11, background: '#e7f6ec', color: '#1a7f37' }}>{t('Collected')}</span>
                : <span className="pill" style={{ fontSize: 11 }}>{a.status}</span>}
              <span style={{ fontWeight: 700 }}>{rupees(a.totalPaise)}</span>
            </div>
            {a.lines.map((l) => <div key={l.id} style={{ fontSize: 13, color: 'var(--ink-2)' }}>{l.label} × {l.quantity}</div>)}
          </Card>
        ))}
    </div>
  );
}

function ReportCardsTab({ studentId }: { studentId: string }) {
  const t = useT();
  const [rows, setRows] = useState<PortalReportCard[] | null>(null);
  const [open, setOpen] = useState<PortalReportCard | null>(null);
  useEffect(() => { setRows(null); portalApi.reportCards(studentId).then((r) => setRows(asList(r))).catch(() => setRows([])); }, [studentId]);
  if (!rows) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  if (rows.length === 0) return <Card muted>{t('No report cards published yet.')}</Card>;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((c) => (
        <Card key={c.id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{c.term} · {c.academicYear}</div>
              <div style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                {c.percentage != null ? `${c.percentage}%` : ''}{c.overallGrade ? ` · Grade ${c.overallGrade}` : ''}
              </div>
            </div>
            <button className="btn" onClick={() => setOpen(open?.id === c.id ? null : c)}>{open?.id === c.id ? 'Hide' : 'View'}</button>
          </div>
          {open?.id === c.id && (
            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 12 }}>
              <thead>
                <tr style={{ textAlign: 'left', fontSize: 12, color: 'var(--ink-3)' }}>
                  <th style={{ padding: '4px' }}>{t('Subject')}</th><th>{t('Marks')}</th><th>{t('Max')}</th><th>{t('Grade')}</th>
                </tr>
              </thead>
              <tbody>
                {c.lines.map((l) => (
                  <tr key={l.subjectId + l.subjectName} style={{ borderTop: '1px solid var(--line)' }}>
                    <td style={{ padding: '4px' }}>{l.subjectName}</td>
                    <td>{l.isAbsent ? 'Absent' : l.marks ?? '—'}</td>
                    <td>{l.maxMarks}</td>
                    <td>{l.grade ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      ))}
    </div>
  );
}

function RemarksTab({ studentId }: { studentId: string }) {
  const t = useT();
  const [rows, setRows] = useState<PortalRemark[] | null>(null);
  useEffect(() => { setRows(null); portalApi.remarks(studentId).then((r) => setRows(asList(r))).catch(() => setRows([])); }, [studentId]);
  if (!rows) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  if (rows.length === 0) return <Card muted>{t('No remarks yet.')}</Card>;
  const tone: Record<string, string> = { Appreciation: '#1a7f37', Concern: '#c1272d', Behaviour: '#b26a00', Academic: '#2456b2', General: 'var(--ink-2)' };
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((r) => (
        <Card key={r.id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <span style={{ fontWeight: 600, color: tone[r.kind] ?? 'var(--ink-2)' }}>{r.kind}</span>
            <span style={{ fontSize: 12, color: 'var(--ink-3)', marginLeft: 'auto' }}>{formatDate(r.createdAt)}</span>
          </div>
          <div style={{ fontSize: 14 }}>{r.note}</div>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>— {r.authorName}</div>
        </Card>
      ))}
    </div>
  );
}

function DownloadsTab() {
  const t = useT();
  const [rows, setRows] = useState<PortalDocument[] | null>(null);
  useEffect(() => { portalApi.downloads().then((r) => setRows(asList(r))).catch(() => setRows([])); }, []);
  if (!rows) return <p style={{ color: 'var(--ink-3)' }}>Loading…</p>;
  if (rows.length === 0) return <Card muted>{t('No files to download yet.')}</Card>;
  const fmtSize = (b: number) => b < 1024 ? `${b} B` : b < 1048576 ? `${(b / 1024).toFixed(0)} KB` : `${(b / 1048576).toFixed(1)} MB`;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((d) => (
        <Card key={d.id}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600 }}>{d.title} <span className="pill" style={{ fontSize: 11 }}>{d.category}</span></div>
              <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>{d.fileName} · {fmtSize(d.sizeBytes)}</div>
            </div>
            <button className="btn btn-accent" onClick={() => downloadFile('portal', d.id, d.fileName)}>{t('Download')}</button>
          </div>
        </Card>
      ))}
    </div>
  );
}

function TabBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick} style={{
      background: 'none', border: 'none', cursor: 'pointer', padding: '8px 14px', fontSize: 14,
      color: active ? 'var(--ink)' : 'var(--ink-3)', fontWeight: active ? 600 : 400,
      borderBottom: active ? '2px solid var(--accent,#C1272D)' : '2px solid transparent',
    }}>{children}</button>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="card" style={{ padding: '12px 16px', minWidth: 110 }}>
      <div style={{ fontSize: 11, color: 'var(--ink-3)', textTransform: 'uppercase', letterSpacing: '.03em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, marginTop: 2 }}>{value}</div>
    </div>
  );
}

function Card({ children, muted }: { children?: ReactNode; muted?: boolean }) {
  return <div className="card" style={{ padding: muted ? 24 : 16, textAlign: muted ? 'center' : 'left', color: muted ? 'var(--ink-2)' : 'var(--ink)' }}>{children}</div>;
}
